<!--
  ItpChecklist — ITP items 6-31 (spec Appendix A, per-pour half).

  Each row carries the point type badge, the status (done / N-A / failed), the
  initials and server-stamped time, and notes.

  Hold points
  -----------
  Spec: "An item whose `point_type` contains `H` and whose status is `failed`
  triggers a blocking prompt: production is to stop and Kiewit QC be notified
  before restart. The pour cannot be marked closed with an open failed Hold
  point; it must be resolved or paired with a stop-hold event."

  That prompt is rendered as a persistent, high-contrast banner on the item AND
  at the top of the section — never a modal, because the spec also says "No
  modal dialogs that can trap a user mid-pour". A modal would also disappear on
  reload, and this warning must survive one: it is re-derived from the data
  every render, not from a one-shot event. The server's `hold_point_triggered`
  flag is honoured too, so the prompt fires the instant the status is set.

  Hold rows are visually distinct at rest as well as when failed — a thick left
  rule and an inverted H badge — per the spec's UX rule "Hold points visually
  distinct from ordinary lines".
-->
<script lang="ts">
  import type { ItpItem, ItpStatus, UpdateItpItemRequest } from '../../lib/types';
  import InitialsTimeField from '../../lib/fields/InitialsTimeField.svelte';
  import NotesField from '../../lib/fields/NotesField.svelte';
  import YesNoField from '../../lib/fields/YesNoField.svelte';
  import { clearDraft, loadDraft, saveDraft } from '../../lib/fields/local-draft';

  interface Props {
    pourId: number;
    /** ITP items 6-31; rendered by item_no. */
    items: ItpItem[];
    /** Session shift name — powers the one-tap initials fill. */
    shiftName?: string | null;
    readonly?: boolean;
    /**
     * PATCH /api/pours/:pourId/itp-items/:itemId. May resolve to the API's
     * ItpItemResponse; its `hold_point_triggered` flag is used to raise the
     * prompt immediately.
     */
    onUpdate?: (
      itemId: number,
      body: UpdateItpItemRequest
    ) => Promise<{ hold_point_triggered?: boolean } | void>;
    /** Optional: jump to the stop-hold record, which is how a failed Hold point is paired. */
    onRecordStopHold?: (item: ItpItem) => void;
  }

  let {
    pourId,
    items,
    shiftName = null,
    readonly = false,
    onUpdate,
    onRecordStopHold
  }: Props = $props();

  /* ---------------------------------------------------------- point types */

  const POINT_TYPE_LABELS: Record<string, string> = {
    H: 'Hold',
    W: 'Witness',
    S: 'Surveillance',
    R: 'Review'
  };

  /** The spec's rule verbatim: point_type containing 'H' is a Hold point. */
  function isHold(pointType: string): boolean {
    return pointType.toUpperCase().includes('H');
  }

  function letters(pointType: string): string[] {
    return pointType
      .toUpperCase()
      .split('/')
      .map((s) => s.trim())
      .filter(Boolean);
  }

  const STATUS_OPTIONS = [
    { value: 'done' as ItpStatus, label: 'Done', tone: 'good' as const },
    { value: 'na' as ItpStatus, label: 'N-A', tone: 'neutral' as const },
    { value: 'failed' as ItpStatus, label: 'Failed', tone: 'bad' as const }
  ];

  /* ------------------------------------------------------------------ draft */

  type Draft = Partial<Pick<ItpItem, 'status' | 'initials' | 'notes'>>;

  const DRAFT_KEY = `itp.${pourId}`;

  interface DraftState {
    values: Record<number, Draft>;
    dirty: Record<number, string[]>;
  }

  const restored = loadDraft<DraftState>(DRAFT_KEY);
  let values = $state<Record<number, Draft>>(restored?.values ?? {});
  let dirty = $state<Record<number, string[]>>(restored?.dirty ?? {});

  let saving = $state<Record<number, boolean>>({});
  let errors = $state<Record<number, string | null>>({});
  let expanded = $state<Record<number, boolean>>({});
  /** Item ids the server has just told us tripped the Hold rule. */
  let triggered = $state<Record<number, boolean>>({});

  type Filter = 'all' | 'outstanding' | 'hold';
  let filter = $state<Filter>('all');

  $effect(() => {
    const snapshot: DraftState = { values: $state.snapshot(values), dirty: $state.snapshot(dirty) };
    if (Object.keys(snapshot.values).length === 0) clearDraft(DRAFT_KEY);
    else saveDraft(DRAFT_KEY, snapshot);
  });

  let rows = $derived(
    [...items]
      .sort((a, b) => a.item_no - b.item_no)
      .map((base) => ({ base, view: { ...base, ...(values[base.id] ?? {}) } as ItpItem }))
  );

  let recorded = $derived(rows.filter((r) => r.view.status !== 'pending').length);

  /**
   * Every failed Hold point, re-derived from the data on every render so the
   * warning survives a reload, a session expiry and a device swap.
   */
  let failedHolds = $derived(
    rows.filter((r) => isHold(r.view.point_type) && r.view.status === 'failed')
  );

  let visible = $derived(
    rows.filter((r) => {
      if (filter === 'outstanding') return r.view.status === 'pending';
      if (filter === 'hold') return isHold(r.view.point_type);
      return true;
    })
  );

  function isDirty(id: number): boolean {
    return (dirty[id]?.length ?? 0) > 0;
  }

  function set<K extends keyof Draft>(id: number, key: K, v: Draft[K]) {
    if (readonly) return;
    if (!values[id]) values[id] = {};
    values[id][key] = v;
    const keys = dirty[id] ?? [];
    if (!keys.includes(key as string)) dirty[id] = [...keys, key as string];
  }

  async function flush(id: number) {
    const pending = dirty[id];
    if (readonly || !pending || pending.length === 0 || saving[id]) return;

    const draft = values[id] ?? {};
    const patch: Record<string, unknown> = {};
    for (const key of pending) patch[key] = (draft as Record<string, unknown>)[key];

    saving[id] = true;
    errors[id] = null;
    try {
      // `unknown` because the prop's return type includes `void`, which TS
      // refuses to let you test for truthiness directly.
      const res: unknown = await onUpdate?.(id, patch as UpdateItpItemRequest);
      if (res && typeof res === 'object' && (res as { hold_point_triggered?: boolean }).hold_point_triggered) {
        triggered[id] = true;
        expanded[id] = true;
      }
      const still = (dirty[id] ?? []).filter(
        (k) => (values[id] as Record<string, unknown>)?.[k] !== patch[k]
      );
      if (still.length === 0) delete dirty[id];
      else dirty[id] = still;
      prune(id);
    } catch (e) {
      // The draft is never discarded because a request failed.
      errors[id] = e instanceof Error ? e.message : 'Could not save. Tap retry.';
    } finally {
      saving[id] = false;
    }
  }

  function prune(id: number) {
    const base = items.find((t) => t.id === id);
    const draft = values[id];
    if (!base || !draft) return;
    for (const k of Object.keys(draft) as (keyof Draft)[]) {
      if (dirty[id]?.includes(k as string)) continue;
      if ((base as unknown as Record<string, unknown>)[k as string] === draft[k]) delete draft[k];
    }
    if (Object.keys(draft).length === 0) delete values[id];
  }

  /** One tap sets the status and flushes it; there is no separate save step. */
  function setStatus(id: number, status: ItpStatus | null) {
    set(id, 'status', status ?? 'pending');
    flush(id);
  }

  function timeOf(item: ItpItem): string | null {
    return item.recorded_at;
  }
</script>

<section class="itp" aria-label="ITP checklist">
  <header class="head">
    <h2>ITP items 6–31</h2>
    <span class="progress">{recorded} of {rows.length} recorded</span>
  </header>

  <!-- The section-level Hold prompt. Persistent, never modal, re-derived from
       the data so it cannot be dismissed away by a reload. -->
  {#if failedHolds.length > 0}
    <div class="holdalert" role="alert">
      <p class="holdalert-title">
        Stop production. Notify Kiewit QC before restart.
      </p>
      <p class="holdalert-body">
        {failedHolds.length}
        failed Hold {failedHolds.length === 1 ? 'point' : 'points'}. This pour cannot be closed until
        each is resolved or paired with a stop-hold event.
      </p>
      <ul class="holdalert-list">
        {#each failedHolds as r (r.base.id)}
          <li>
            <button type="button" class="jump" onclick={() => (expanded[r.base.id] = true)}>
              {r.view.item_no}. {r.view.title}
            </button>
          </li>
        {/each}
      </ul>
    </div>
  {/if}

  <div class="filters" role="group" aria-label="Filter items">
    <button type="button" class:on={filter === 'all'} onclick={() => (filter = 'all')}>
      All ({rows.length})
    </button>
    <button
      type="button"
      class:on={filter === 'outstanding'}
      onclick={() => (filter = 'outstanding')}
    >
      Outstanding ({rows.length - recorded})
    </button>
    <button type="button" class:on={filter === 'hold'} onclick={() => (filter = 'hold')}>
      Hold points ({rows.filter((r) => isHold(r.view.point_type)).length})
    </button>
  </div>

  {#if visible.length === 0}
    <p class="empty">Nothing matches this filter.</p>
  {/if}

  <ol class="rows">
    {#each visible as { base, view } (base.id)}
      {@const hold = isHold(view.point_type)}
      {@const failedHold = hold && view.status === 'failed'}
      {@const open = expanded[base.id] ?? false}
      <li
        class="item"
        class:hold={hold}
        class:failed-hold={failedHold}
        class:unsaved={isDirty(base.id)}
        class:done={view.status === 'done'}
      >
        <div class="itemhead">
          <span class="no">{view.item_no}</span>

          <span class="types">
            {#each letters(view.point_type) as l (l)}
              <span
                class="type"
                class:h={l === 'H'}
                title={POINT_TYPE_LABELS[l] ?? l}
                aria-label={POINT_TYPE_LABELS[l] ?? l}>{l}</span
              >
            {/each}
          </span>

          <button
            type="button"
            class="title"
            aria-expanded={open}
            onclick={() => (expanded[base.id] = !open)}
          >
            {view.title}
          </button>

          <span class="state" aria-live="polite">
            {#if saving[base.id]}
              <span class="pill saving">Saving…</span>
            {:else if isDirty(base.id)}
              <span class="pill dirty">Unsaved</span>
              <button type="button" class="retry" onclick={() => flush(base.id)}>Save</button>
            {/if}
          </span>
        </div>

        <div class="statusrow">
          <YesNoField
            label="Status"
            hideLabel
            compact
            value={view.status === 'pending' ? null : view.status}
            options={STATUS_OPTIONS}
            disabled={readonly}
            onValue={(v) => setStatus(base.id, v)}
          />
        </div>

        {#if errors[base.id]}
          <p class="banner bad" role="alert">
            {errors[base.id]}
            <button type="button" class="retry" onclick={() => flush(base.id)}>Retry</button>
          </p>
        {/if}

        <!-- The per-item Hold prompt. Shown whenever a Hold point stands
             failed, and immediately on the server's hold_point_triggered. -->
        {#if failedHold || triggered[base.id]}
          <div class="itemhold" role="alert">
            <p><strong>Hold point failed.</strong> Stop production and notify Kiewit QC before restart.</p>
            {#if onRecordStopHold && !readonly}
              <button type="button" class="cta" onclick={() => onRecordStopHold?.(base)}>
                Record stop / hold event
              </button>
            {/if}
          </div>
        {/if}

        {#if open}
          <div class="body">
            <InitialsTimeField
              label="SCI initials / time"
              initials={view.initials}
              time={timeOf(base)}
              {shiftName}
              timeEditable={false}
              disabled={readonly}
              compact
              onInitials={(v) => set(base.id, 'initials', v)}
              onCommit={() => flush(base.id)}
            />
            <NotesField
              value={view.notes}
              collapsible
              disabled={readonly}
              onValue={(v) => set(base.id, 'notes', v)}
              onCommit={() => flush(base.id)}
            />
          </div>
        {/if}
      </li>
    {/each}
  </ol>

  <p class="legend">
    <strong>H</strong> Hold · <strong>W</strong> Witness · <strong>S</strong> Surveillance ·
    <strong>R</strong> Review
  </p>
</section>

<style>
  .itp {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .head {
    display: flex;
    align-items: baseline;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  h2 {
    margin: 0;
    font-size: 1.0625rem;
    font-weight: 800;
  }

  .progress {
    font-size: 0.8125rem;
    font-weight: 700;
    color: var(--fg-muted, #3f4750);
    margin-left: auto;
  }

  /* ------------------------------------------------- the Hold prompt ---- */

  .holdalert {
    border: 3px solid var(--bad, #b3261e);
    border-radius: 0.625rem;
    background: var(--bad-bg, #fdecea);
    padding: 0.75rem;
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
  }

  .holdalert-title {
    margin: 0;
    font-size: 1.0625rem;
    font-weight: 900;
    letter-spacing: 0.01em;
    color: var(--bad, #b3261e);
    text-transform: uppercase;
  }

  .holdalert-body {
    margin: 0;
    font-size: 0.9375rem;
    font-weight: 600;
    color: var(--fg, #10151b);
  }

  .holdalert-list {
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .jump {
    text-align: left;
    width: 100%;
    min-height: 2.75rem;
    padding: 0.375rem 0.5rem;
    font: inherit;
    font-size: 0.9375rem;
    font-weight: 700;
    border: 2px solid var(--bad, #b3261e);
    border-radius: 0.5rem;
    background: var(--surface, #fff);
    color: var(--bad, #b3261e);
    cursor: pointer;
    touch-action: manipulation;
  }

  /* --------------------------------------------------------- filters ---- */

  .filters {
    display: flex;
    gap: 0.375rem;
    flex-wrap: wrap;
  }

  .filters button {
    min-height: 2.75rem;
    padding: 0 0.75rem;
    font: inherit;
    font-size: 0.875rem;
    font-weight: 700;
    border: 2px solid var(--border, #9aa3ad);
    border-radius: 999px;
    background: var(--surface, #fff);
    color: var(--fg-muted, #3f4750);
    cursor: pointer;
    touch-action: manipulation;
  }

  .filters button.on {
    background: var(--fg, #10151b);
    border-color: var(--fg, #10151b);
    color: #fff;
  }

  /* ----------------------------------------------------------- items ---- */

  .empty {
    margin: 0;
    padding: 1rem;
    border: 2px dashed var(--border, #9aa3ad);
    border-radius: 0.5rem;
    color: var(--fg-muted, #3f4750);
  }

  .rows {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .item {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.5rem;
    border: 2px solid var(--border, #9aa3ad);
    border-left-width: 4px;
    border-radius: 0.625rem;
    background: var(--surface, #fff);
  }

  /* Hold points are distinct at rest, not only when they fail. */
  .item.hold {
    border-left-width: 10px;
    border-left-color: var(--fg, #10151b);
    background: var(--hold-bg, #f7f4ee);
  }

  .item.failed-hold {
    border-color: var(--bad, #b3261e);
    border-left-color: var(--bad, #b3261e);
    background: var(--bad-bg, #fdecea);
  }

  .item.unsaved {
    border-color: var(--warn, #a35a00);
  }

  .item.done {
    border-left-color: var(--good, #1a7f45);
  }

  .itemhead {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  .no {
    font-weight: 900;
    font-variant-numeric: tabular-nums;
    min-width: 1.5rem;
  }

  .types {
    display: flex;
    gap: 0.15rem;
  }

  .type {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.5rem;
    height: 1.5rem;
    border-radius: 0.3rem;
    font-size: 0.75rem;
    font-weight: 900;
    border: 2px solid var(--border, #9aa3ad);
    color: var(--fg-muted, #3f4750);
    background: var(--surface, #fff);
  }

  /* Inverted H — a Hold point reads differently at a glance. */
  .type.h {
    background: var(--fg, #10151b);
    border-color: var(--fg, #10151b);
    color: #fff;
  }

  .title {
    flex: 1 1 10rem;
    min-width: 0;
    text-align: left;
    min-height: 2.75rem;
    padding: 0;
    border: 0;
    background: transparent;
    font: inherit;
    font-size: 0.9375rem;
    font-weight: 700;
    line-height: 1.25;
    color: var(--fg, #10151b);
    cursor: pointer;
    touch-action: manipulation;
  }

  .title:focus-visible {
    outline: 3px solid var(--accent, #0b63c5);
    outline-offset: 2px;
  }

  .statusrow {
    padding-left: 0.1rem;
  }

  .state {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    margin-left: auto;
  }

  .pill {
    font-size: 0.75rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 0.15rem 0.45rem;
    border-radius: 999px;
  }

  .pill.dirty {
    background: var(--warn-bg, #fff6e6);
    color: var(--warn, #8a4b00);
  }

  .pill.saving {
    background: var(--surface-muted, #eef1f4);
    color: var(--fg-muted, #3f4750);
  }

  .banner {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin: 0;
    padding: 0.5rem 0.625rem;
    border-radius: 0.5rem;
    font-size: 0.875rem;
    font-weight: 600;
  }

  .banner.bad {
    background: var(--bad-bg, #fdecea);
    color: var(--bad, #b3261e);
  }

  .itemhold {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.625rem;
    border: 3px solid var(--bad, #b3261e);
    border-radius: 0.5rem;
    background: var(--surface, #fff);
  }

  .itemhold p {
    margin: 0;
    font-size: 0.9375rem;
    font-weight: 700;
    line-height: 1.3;
    color: var(--bad, #b3261e);
  }

  .cta {
    min-height: 3rem;
    padding: 0 1rem;
    font: inherit;
    font-weight: 800;
    border: 2px solid var(--bad, #b3261e);
    border-radius: 0.5rem;
    background: var(--bad, #b3261e);
    color: #fff;
    cursor: pointer;
    touch-action: manipulation;
  }

  .retry {
    min-height: 2.5rem;
    padding: 0 0.75rem;
    font: inherit;
    font-weight: 800;
    border: 2px solid currentColor;
    border-radius: 0.4rem;
    background: transparent;
    color: inherit;
    cursor: pointer;
    touch-action: manipulation;
  }

  .body {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding-top: 0.25rem;
    border-top: 2px solid var(--border-soft, #dde2e7);
  }

  .legend {
    margin: 0;
    font-size: 0.8125rem;
    color: var(--fg-muted, #3f4750);
  }
</style>
